<template>
  <v-sheet outlined rounded class="queue-health" :class="{ 'queue-health--alert': noWorkers }">
    <div class="queue-health__row">
      <!-- Slots -->
      <div
        class="queue-health__block queue-health__slots"
        :title="`Capacity source: ${slotUsage.source || 'unknown'}`"
      >
        <div class="queue-health__label">Slots</div>
        <div class="queue-health__value">
          <span class="queue-health__slots-text">{{ slotsText }}</span>
          <v-progress-linear
            v-if="slotUsage.capacity"
            :value="slotsPct"
            height="6"
            rounded
            :color="slotsPct >= 100 ? 'warning' : 'primary'"
            class="queue-health__slots-bar"
          />
          <span class="caption grey--text ml-1">({{ slotsSourceLabel }})</span>
        </div>
      </div>

      <v-divider vertical class="mx-3" />

      <!-- Bridge -->
      <div class="queue-health__block">
        <div class="queue-health__label">Progress bridge</div>
        <div class="queue-health__value">
          <v-chip
            x-small
            :color="bridgeChip.color"
            :dark="bridgeChip.dark"
            class="queue-health__bridge"
          >
            <v-icon x-small left>{{ bridgeChip.icon }}</v-icon>
            {{ bridgeChip.text }}
          </v-chip>
          <span v-if="lastMessageAge" class="caption grey--text ml-1"
            >last message {{ lastMessageAge }}</span
          >
        </div>
      </div>

      <v-divider vertical class="mx-3" />

      <!-- Counters -->
      <div class="queue-health__block">
        <div class="queue-health__label">Runs</div>
        <div class="queue-health__value">
          <v-chip
            x-small
            outlined
            :color="health.degraded_runs ? 'warning' : 'grey'"
            class="mr-1 queue-health__degraded"
          >
            {{ health.degraded_runs || 0 }} degraded
          </v-chip>
          <v-chip
            x-small
            outlined
            :color="health.lost_runs ? 'error' : 'grey'"
            class="mr-1 queue-health__lost"
          >
            {{ health.lost_runs || 0 }} lost
          </v-chip>
          <span v-if="oldestQueuedAge" class="caption grey--text queue-health__oldest"
            >oldest waiting {{ oldestQueuedAge }}</span
          >
          <v-chip
            v-if="storage"
            x-small
            outlined
            :color="storage.orphanChunks ? 'warning' : 'grey'"
            class="ml-1 queue-health__storage"
            :title="storage.title"
          >
            {{ storage.text }}
          </v-chip>
        </div>
      </div>

      <v-divider vertical class="mx-3" />

      <!-- Versions -->
      <div class="queue-health__block">
        <div class="queue-health__label">Versions</div>
        <div class="queue-health__value">
          <v-chip
            x-small
            outlined
            class="mr-1 queue-health__version queue-health__version--api"
            :title="apiVersion.title"
          >
            api {{ apiVersion.text }}
          </v-chip>
          <v-chip
            x-small
            outlined
            class="queue-health__version queue-health__version--worker"
            :color="workerVersion.degraded ? 'warning' : undefined"
            :title="workerVersion.title"
          >
            worker {{ workerVersion.text }}
          </v-chip>
        </div>
      </div>

      <v-spacer />

      <!-- Live -->
      <div class="queue-health__block queue-health__live">
        <v-chip x-small :color="live ? 'success' : 'warning'" dark class="queue-health__live-chip">
          <v-icon x-small left>{{ live ? 'mdi-access-point' : 'mdi-access-point-off' }}</v-icon>
          {{ live ? 'Live' : 'Live updates paused' }}
        </v-chip>
        <span class="caption grey--text ml-2 queue-health__updated">
          <template v-if="lastUpdatedAt">updated {{ formatAge(lastUpdatedAt, now) }}</template>
          <template v-else>not loaded yet</template>
        </span>
        <v-btn
          icon
          small
          class="ml-1 queue-health__refresh"
          :loading="loading"
          title="Refresh"
          aria-label="Refresh"
          @click="$emit('refresh')"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="noWorkers" class="queue-health__banner caption error--text px-3 pb-2">
      <v-icon x-small color="error" class="mr-1">mdi-alert</v-icon>
      No workers available · runs will wait
    </div>
  </v-sheet>
</template>

<script>
import { formatAge, formatBytes } from './queueFormat';

const SLOT_SOURCE_LABELS = Object.freeze({
  workers: 'live workers',
  cap: 'configured cap',
  unknown: 'unknown',
});

function shortSha(sha) {
  return sha ? String(sha).slice(0, 7) : '';
}

/**
 * One-line health bar over the lanes: slots, bridge liveness, degraded / lost counters, oldest
 * queued age, API and worker versions, live indicator with last update and a manual refresh.
 */
export default {
  name: 'QueueHealthStrip',

  props: {
    /** `slots` block of the queue read model ({capacity, in_use, free, source}). */
    slotUsage: {
      type: Object,
      default: () => ({ capacity: null, in_use: 0, free: null, source: 'unknown' }),
    },
    health: {
      type: Object,
      default: () => ({}),
    },
    versions: {
      type: Object,
      default: () => ({ api: null, worker: null }),
    },
    live: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    now: {
      type: Number,
      default: () => Date.now(),
    },
  },

  computed: {
    slotsText() {
      const { capacity, in_use: inUse } = this.slotUsage || {};
      if (capacity == null) return inUse != null ? `${inUse} in use` : 'unknown';
      return `${inUse || 0} / ${capacity}`;
    },
    slotsPct() {
      const { capacity, in_use: inUse } = this.slotUsage || {};
      if (!capacity) return 0;
      return Math.min(100, Math.round(((inUse || 0) / capacity) * 100));
    },
    slotsSourceLabel() {
      return SLOT_SOURCE_LABELS[this.slotUsage?.source] || SLOT_SOURCE_LABELS.unknown;
    },
    noWorkers() {
      return this.slotUsage?.source === 'workers' && this.slotUsage?.capacity === 0;
    },
    bridgeChip() {
      const alive = this.health?.bridge_alive;
      if (alive === true)
        return { text: 'alive', color: 'success', dark: true, icon: 'mdi-check-circle' };
      if (alive === false)
        return { text: 'down', color: 'error', dark: true, icon: 'mdi-close-circle' };
      return { text: 'unknown', color: 'grey lighten-2', dark: false, icon: 'mdi-help-circle' };
    },
    lastMessageAge() {
      return formatAge(this.health?.last_message_at, this.now);
    },
    oldestQueuedAge() {
      return formatAge(this.health?.oldest_queued_at, this.now);
    },
    /** Result storage weight and orphan chunks (M5 X3); absent while the report is unavailable. */
    storage() {
      const storage = this.health?.storage;
      if (!storage) return null;
      const orphanChunks = storage.orphan_chunks || 0;
      const size = formatBytes(storage.total_size_bytes);
      const parts = Object.entries(storage.collections || {}).map(
        ([name, entry]) => `${name}: ${formatBytes(entry?.size_bytes) || '?'}`
      );
      return {
        orphanChunks,
        text: orphanChunks ? `${size || 'storage'} · ${orphanChunks} orphan` : size || 'storage',
        title: [...parts, orphanChunks ? `${orphanChunks} orphan chunk sets` : '']
          .filter(Boolean)
          .join('\n'),
      };
    },
    apiVersion() {
      const api = this.versions?.api;
      if (!api) return { text: 'unknown', title: 'API version not available' };
      const sha = shortSha(api.build_sha);
      return {
        text: `${api.tool_version || '?'}${sha ? ` · ${sha}` : ''}`,
        title: `API (Node) ${api.tool_version || ''} build ${api.build_sha || ''}`,
      };
    },
    workerVersion() {
      const worker = this.versions?.worker;
      if (!worker)
        return {
          text: 'no run yet',
          title: 'No worker has reported an engine block yet',
          degraded: false,
        };
      const sha = shortSha(worker.build_sha);
      const engines = worker.engines || {};
      const engineText = ['gestures', 'coherence']
        .filter((name) => engines[name])
        .map((name) => `${name} ${engines[name]}`)
        .join(' · ');
      return {
        text: `${worker.tool_version || '?'}${sha ? ` · ${sha}` : ''}`,
        title: `Worker ${worker.tool_version || ''} build ${worker.build_sha || ''}${engineText ? ` · ${engineText}` : ''}`,
        degraded: Boolean(worker.degraded),
      };
    },
  },

  methods: {
    formatAge,
  },
};
</script>

<style scoped>
.queue-health {
  padding: 8px 12px;
  margin-bottom: 16px;
}
.queue-health--alert {
  border-color: var(--v-error-base, #ff5252) !important;
}
.queue-health__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 0;
}
.queue-health__block {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.queue-health__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.54);
}
.queue-health__value {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-height: 24px;
}
.queue-health__slots-text {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.queue-health__slots-bar {
  width: 72px;
  margin-left: 8px;
}
.queue-health__live {
  flex-direction: row;
  align-items: center;
}
</style>
